import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Formik, Field, Form, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import {
  Typography,
  Container,
  Grid,
  FormLabel,
  Button,
  CircularProgress,
  TextField
} from '@mui/material';
import Chip from '@mui/material/Chip';
import { LoadingButton } from '@mui/lab';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import axios from 'axios';
import { toast } from 'react-toastify';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Loader from 'src/components/Loader';
import { logUserActivity } from 'src/Redux/actions/activityLogsActions';
import { getActiveCountries } from 'src/api/api';

const validTypes = ['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'];

const validationSchema = Yup.object({
  author: Yup.object({
    name: Yup.string().required('Author name is required.'),
    bio: Yup.string().required('Author bio is required.')
  }),
  boostGrowth: Yup.object({
    heading: Yup.string().required('Heading is required.'),
    subtitle: Yup.string().required('Subtitle is required.'),
    services: Yup.array().of(
      Yup.object({
        label: Yup.string().required('Label is required.'),
        href: Yup.string().required('Link is required.')
      })
    )
  }),
  trendingServices: Yup.array().of(
    Yup.object({
      name: Yup.string().required('Name is required.'),
      detail: Yup.string().required('Detail is required.'),
      href: Yup.string().required('Link is required.')
    })
  )
});

// ----------------------------------------------------------------------

export default function EditBlogDetailSections() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageData, setPageData] = useState(null);
  const [countryLoading, setCountryLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [boostThumbnails, setBoostThumbnails] = useState({});
  const [trendingThumbnails, setTrendingThumbnails] = useState({});
  const [authorThumbnail, setAuthorThumbnail] = useState(null);
  const boostServiceRefs = useRef([]);
  const trendingServiceRefs = useRef([]);

  useEffect(() => {
    setCountryLoading(true);
    getActiveCountries()
      .then((data) => {
        if (data.success) setCountries(data.countries || []);
      })
      .catch(() => setCountries([]))
      .finally(() => setCountryLoading(false));
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setPageLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog-section/${id}`);
        setPageData(res.data?.data);
      } catch (error) {
        toast.error('Failed to load blog detail sections.', {
          position: 'top-center',
          autoClose: 2000
        });
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      await axios.put(`${process.env.REACT_APP_API_URL}/blog-section/${id}`, {
        author: values.author,
        boostGrowth: values.boostGrowth,
        trendingServices: values.trendingServices
      });
      toast.success('Blog detail sections updated successfully.', {
        position: 'top-center',
        autoClose: 2000
      });
      dispatch(logUserActivity('UPDATED_BLOG_DETAIL_SECTIONS'));
      setTimeout(() => navigate('/dashboard/blogs-detail-section'), 2000);
    } catch (error) {
      toast.error('Failed to update blog detail sections.', {
        position: 'top-center',
        autoClose: 2000
      });
    } finally {
      setLoading(false);
    }
  };
  const initialValues = {
    countryId: pageData?.countryId || '',
    author: {
      name: pageData?.author?.name || '',
      role: pageData?.author?.role || '',
      bio: pageData?.author?.bio || '',
      imageSrc: pageData?.author?.imageSrc || '',
      email: pageData?.author?.email || '',
      label: pageData?.author?.label || ''
    },
    boostGrowth: {
      heading: pageData?.boostGrowth?.heading || '',
      subtitle: pageData?.boostGrowth?.subtitle || '',
      services: pageData?.boostGrowth?.services || []
    },
    trendingServices: pageData?.trendingServices || []
  };

  return (
    <Container>
      <Grid container justifyContent="center">
        <Grid item lg={10} md={10} xs={10} sm={10}>
          {pageLoading ? (
            <Loader />
          ) : (
            <Card
              sx={{
                minWidth: 275,
                boxShadow: 3,
                borderRadius: '10px',
                border: '1px solid #929eaa6b'
              }}
            >
              <CardContent>
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue }) => (
                    <Form autoComplete="off" style={{ padding: 'inherit' }}>
                      <Typography variant="h4" gutterBottom mb={2}>
                        Edit Blog Detail Sections
                      </Typography>

                      {/* Country */}
                      <div className="input-section">
                        <FormLabel>Country</FormLabel>
                        {countryLoading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <TextField
                            fullWidth
                            sx={{ mt: 1 }}
                            value={countries.find((c) => c._id === values.countryId)?.countryName || ''}
                            InputProps={{ readOnly: true }}
                          />
                        )}
                      </div>

                      {/* Author Section */}
                      <SectionName name="Author Section" />

                      <div className="input-section">
                        <FormLabel htmlFor="author.label">Label</FormLabel>
                        <Field
                          id="author.label"
                          name="author.label"
                          type="text"
                          placeholder="ABOUT THE AUTHOR"
                          className="input-style"
                        />
                        <ErrorMessage name="author.label" component="div" className="error-color" />
                      </div>

                      <div className="input-section">
                        <FormLabel htmlFor="author.name">Name</FormLabel>
                        <Field
                          id="author.name"
                          name="author.name"
                          type="text"
                          placeholder="Author Name"
                          className="input-style"
                        />
                        <ErrorMessage name="author.name" component="div" className="error-color" />
                      </div>

                      <div className="input-section">
                        <FormLabel htmlFor="author.role">Role</FormLabel>
                        <Field
                          id="author.role"
                          name="author.role"
                          type="text"
                          placeholder="Author Role"
                          className="input-style"
                        />
                        <ErrorMessage name="author.role" component="div" className="error-color" />
                      </div>

                      <div className="input-section">
                        <FormLabel htmlFor="author.bio">Bio</FormLabel>
                        <Field
                          id="author.bio"
                          name="author.bio"
                          as="textarea"
                          rows={4}
                          placeholder="Enter Author Bio"
                          className="input-style"
                        />
                        <ErrorMessage name="author.bio" component="div" className="error-color" />
                      </div>

                      <div className="input-section">
                        <FormLabel htmlFor="author.email">Email / Link</FormLabel>
                        <Field
                          id="author.email"
                          name="author.email"
                          type="text"
                          placeholder="email@example.com"
                          className="input-style"
                        />
                        <ErrorMessage name="author.email" component="div" className="error-color" />
                      </div>

                      <div className="input-section">
                        <FormLabel>Author Image</FormLabel>
                        <Grid container mb={1}>
                          <Grid xs={12} sm={12} md={12} lg={12} mt={2}>
                            <FormLabel>Current Image</FormLabel>
                            {pageLoading ? (
                              <CircularProgress />
                            ) : (
                              values.author.imageSrc ? (
                                <LazyLoadImage
                                  alt="Current Author Image"
                                  src={`${process.env.REACT_APP_NODE_ROOT}/uploads/images/${values.author.imageSrc}`}
                                  width={100}
                                  height={100}
                                />
                              ) : (
                                <Typography variant="body1" component="i" display="block">
                                  No Image to display.
                                </Typography>
                              )
                            )}
                          </Grid>
                          <Grid xs={12} sm={12} md={4} lg={4} mt={2} mr={2}>
                            <FormLabel>New Image</FormLabel>
                            {authorThumbnail ? (
                              <img
                                alt="new author image"
                                src={authorThumbnail}
                                width={100}
                                height={100}
                                style={{ objectFit: 'contain', objectPosition: 'top center' }}
                              />
                            ) : (
                              <Typography variant="body1" component="i" display="block">
                                No image file selected.
                              </Typography>
                            )}
                          </Grid>
                          <Grid xs={12} sm={12} md={4} lg={4} mt={2}>
                            <FormLabel>Upload New Image</FormLabel>
                            <TextField
                              sx={{ mt: 1 }}
                              id="author.imageSrc"
                              name="author.imageSrc"
                              type="file"
                              onChange={(e) => {
                                const image = e.target.files[0];
                                if (!validTypes.includes(image.type)) {
                                  toast.error('Please select an image, allowed file types are .svg, .png, .jpg & .jpeg.');
                                  return false;
                                }
                                const fileReader = new FileReader();
                                fileReader.onload = () => {
                                  if (fileReader.readyState === 2) {
                                    setFieldValue('author.imageSrc', fileReader.result);
                                    setAuthorThumbnail(fileReader.result);
                                  }
                                };
                                fileReader.readAsDataURL(image);
                              }}
                            />
                          </Grid>
                        </Grid>
                      </div>

                       {/* Trending Services Section */}
                      <div>
                        <SectionName name="Trending Services" />
                        <FieldArray name="trendingServices">
                          {({ remove, push }) => (
                            <div>
                              {values.trendingServices.map((service, idx) => (
                                <div className="row" key={idx} ref={(el) => (trendingServiceRefs.current[idx] = el)}>
                                  <div className="row">
                                    <div className="col button-style">
                                      <Typography variant="h6" gutterBottom mt={1}>
                                        Trending Service {idx + 1}
                                      </Typography>

                                      <div className="input-section">
                                        <FormLabel htmlFor={`trendingServices[${idx}].name`}>
                                          Name
                                        </FormLabel>
                                        <Field
                                          id={`trendingServices[${idx}].name`}
                                          name={`trendingServices[${idx}].name`}
                                          type="text"
                                          placeholder="Enter name"
                                          className="input-style"
                                        />
                                        <ErrorMessage
                                          name={`trendingServices[${idx}].name`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>

                                      <div className="input-section">
                                        <FormLabel htmlFor={`trendingServices[${idx}].detail`}>
                                          Detail
                                        </FormLabel>
                                        <Field
                                          id={`trendingServices[${idx}].detail`}
                                          name={`trendingServices[${idx}].detail`}
                                          type="text"
                                          placeholder="Enter Detail"
                                          className="input-style"
                                        />
                                        <ErrorMessage
                                          name={`trendingServices[${idx}].detail`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>

                                      <div className="input-section">
                                        <FormLabel htmlFor={`trendingServices[${idx}].href`}>
                                          Link (href)
                                        </FormLabel>
                                        <Field
                                          id={`trendingServices[${idx}].href`}
                                          name={`trendingServices[${idx}].href`}
                                          type="text"
                                          placeholder="Enter href"
                                          className="input-style"
                                        />
                                        <ErrorMessage
                                          name={`trendingServices[${idx}].href`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>

                                      <div className="input-section">
                                        <FormLabel htmlFor={`trendingServices[${idx}].src`}>
                                          Icon Image
                                        </FormLabel>
                                        <Grid container mb={1}>
                                          <Grid xs={12} sm={12} md={12} lg={12} mt={2}>
                                            <FormLabel>Current Image</FormLabel>
                                            {loading ? (
                                              <CircularProgress />
                                            ) : (
                                              service.src ? (
                                                <LazyLoadImage
                                                  alt="Current Image"
                                                  src={`${process.env.REACT_APP_NODE_ROOT}/uploads/images/${service.src}`}
                                                  width={100}
                                                  height={100}
                                                />
                                              ) : (
                                                <Typography variant="body1" component="i" display="block">
                                                  No Image to display.
                                                </Typography>
                                              )
                                            )}
                                          </Grid>
                                          <Grid xs={12} sm={12} md={4} lg={4} mt={2} mr={2}>
                                            <FormLabel>New Image</FormLabel>
                                            {trendingThumbnails[idx] ? (
                                              <img
                                                alt="new image"
                                                src={trendingThumbnails[idx]}
                                                width={100}
                                                height={100}
                                                style={{ objectFit: 'contain', objectPosition: 'top center' }}
                                              />
                                            ) : (
                                              <Typography variant="body1" component="i" display="block">
                                                No image file selected.
                                              </Typography>
                                            )}
                                          </Grid>
                                          <Grid xs={12} sm={12} md={4} lg={4} mt={2}>
                                            <FormLabel>Upload New Icon Image</FormLabel>
                                            <TextField
                                              sx={{ mt: 1 }}
                                              id={`trendingServices[${idx}].src`}
                                              name={`trendingServices[${idx}].src`}
                                              type="file"
                                              onChange={(e) => {
                                                const image = e.target.files[0];
                                                if (!validTypes.includes(image.type)) {
                                                  toast.error('Please select an image, allowed file types are .svg, .png, .jpg & .jpeg.');
                                                  return false;
                                                }
                                                const fileReader = new FileReader();
                                                fileReader.onload = () => {
                                                  if (fileReader.readyState === 2) {
                                                    setFieldValue(`trendingServices[${idx}].src`, fileReader.result);
                                                    setTrendingThumbnails((prev) => ({ ...prev, [idx]: fileReader.result }));
                                                  }
                                                };
                                                fileReader.readAsDataURL(image);
                                              }}
                                            />
                                          </Grid>
                                        </Grid>
                                        <ErrorMessage
                                          name={`trendingServices[${idx}].src`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>
                                    </div>

                                    <div className="col button-style">
                                      <div>
                                        <Button
                                          type="button"
                                          variant="contained"
                                          color="secondary"
                                          sx={{ mt: 2, mb: 2, mr: 1 }}
                                          onClick={() => {
                                            push({ name: '', detail: '', href: '', src: '' });
                                            setTimeout(() => {
                                              const newIdx = values.trendingServices.length;
                                              trendingServiceRefs.current[newIdx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }, 50);
                                          }}
                                        >
                                          Add Service
                                        </Button>
                                        {idx !== 0 && (
                                          <Button
                                            type="button"
                                            sx={{ mt: 2, ml: 2, mb: 2 }}
                                            variant="contained"
                                            color="error"
                                            onClick={() => remove(idx)}
                                            className="home-section-delete-button"
                                          >
                                            Delete Service
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {values.trendingServices.length === 0 && (
                                <Button
                                  type="button"
                                  variant="contained"
                                  color="secondary"
                                  sx={{ mt: 1, mb: 2 }}
                                  onClick={() => {
                                    push({ name: '', detail: '', href: '', src: '' });
                                    setTimeout(() => {
                                      trendingServiceRefs.current[0]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 50);
                                  }}
                                >
                                  Add Service
                                </Button>
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </div>

                      {/* Boost Growth Section */}
                      <SectionName name="Boost Growth Section" />

                      <div className="input-section">
                        <FormLabel htmlFor="boostGrowth.heading">Heading</FormLabel>
                        <Field
                          id="boostGrowth.heading"
                          name="boostGrowth.heading"
                          type="text"
                          placeholder="Enter heading"
                          className="input-style"
                        />
                        <ErrorMessage
                          name="boostGrowth.heading"
                          component="div"
                          className="error-color"
                        />
                      </div>

                      <div className="input-section">
                        <FormLabel htmlFor="boostGrowth.subtitle">Subtitle</FormLabel>
                        <Field
                          id="boostGrowth.subtitle"
                          name="boostGrowth.subtitle"
                          type="text"
                          placeholder="Enter subtitle"
                          className="input-style"
                        />
                        <ErrorMessage
                          name="boostGrowth.subtitle"
                          component="div"
                          className="error-color"
                        />
                      </div>

                      {/* Boost Growth Services */}
                      <div>
                        <SectionName name="Boost Growth Services" />
                        <FieldArray name="boostGrowth.services">
                          {({ remove, push }) => (
                            <div>
                              {values.boostGrowth.services.map((service, idx) => (
                                <div className="row" key={idx} ref={(el) => (boostServiceRefs.current[idx] = el)}>
                                  <div className="row">
                                    <div className="col button-style">
                                      <Typography variant="h6" gutterBottom mt={1}>
                                        Service {idx + 1}
                                      </Typography>

                                      <div className="input-section">
                                        <FormLabel
                                          htmlFor={`boostGrowth.services[${idx}].label`}
                                        >
                                          Label
                                        </FormLabel>
                                        <Field
                                          id={`boostGrowth.services[${idx}].label`}
                                          name={`boostGrowth.services[${idx}].label`}
                                          type="text"
                                          placeholder="Enter label"
                                          className="input-style"
                                        />
                                        <ErrorMessage
                                          name={`boostGrowth.services[${idx}].label`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>

                                      <div className="input-section">
                                        <FormLabel
                                          htmlFor={`boostGrowth.services[${idx}].href`}
                                        >
                                          Link (href)
                                        </FormLabel>
                                        <Field
                                          id={`boostGrowth.services[${idx}].href`}
                                          name={`boostGrowth.services[${idx}].href`}
                                          type="text"
                                          placeholder="Enter href"
                                          className="input-style"
                                        />
                                        <ErrorMessage
                                          name={`boostGrowth.services[${idx}].href`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>

                                      <div className="input-section">
                                        <FormLabel
                                          htmlFor={`boostGrowth.services[${idx}].src`}
                                        >
                                          Icon Image
                                        </FormLabel>
                                        <Grid container mb={1}>
                                          <Grid xs={12} sm={12} md={12} lg={12} mt={2}>
                                            <FormLabel>Current Image</FormLabel>
                                            {loading ? (
                                              <CircularProgress />
                                            ) : (
                                              service.src ? (
                                                <LazyLoadImage
                                                  alt="Current Image"
                                                  src={`${process.env.REACT_APP_NODE_ROOT}/uploads/images/${service.src}`}
                                                  width={100}
                                                  height={100}
                                                />
                                              ) : (
                                                <Typography variant="body1" component="i" display="block">
                                                  No Image to display.
                                                </Typography>
                                              )
                                            )}
                                          </Grid>
                                          <Grid xs={12} sm={12} md={4} lg={4} mt={2} mr={2}>
                                            <FormLabel>New Image</FormLabel>
                                            {boostThumbnails[idx] ? (
                                              <img
                                                alt="new image"
                                                src={boostThumbnails[idx]}
                                                width={100}
                                                height={100}
                                                style={{ objectFit: 'contain', objectPosition: 'top center' }}
                                              />
                                            ) : (
                                              <Typography variant="body1" component="i" display="block">
                                                No image file selected.
                                              </Typography>
                                            )}
                                          </Grid>
                                          <Grid xs={12} sm={12} md={4} lg={4} mt={2}>
                                            <FormLabel>Upload New Icon Image</FormLabel>
                                            <TextField
                                              sx={{ mt: 1 }}
                                              id={`boostGrowth.services[${idx}].src`}
                                              name={`boostGrowth.services[${idx}].src`}
                                              type="file"
                                              onChange={(e) => {
                                                const image = e.target.files[0];
                                                if (!validTypes.includes(image.type)) {
                                                  toast.error('Please select an image, allowed file types are .svg, .png, .jpg & .jpeg.');
                                                  return false;
                                                }
                                                const fileReader = new FileReader();
                                                fileReader.onload = () => {
                                                  if (fileReader.readyState === 2) {
                                                    setFieldValue(`boostGrowth.services[${idx}].src`, fileReader.result);
                                                    setBoostThumbnails((prev) => ({ ...prev, [idx]: fileReader.result }));
                                                  }
                                                };
                                                fileReader.readAsDataURL(image);
                                              }}
                                            />
                                          </Grid>
                                        </Grid>
                                        <ErrorMessage
                                          name={`boostGrowth.services[${idx}].src`}
                                          component="div"
                                          className="error-color"
                                        />
                                      </div>
                                    </div>

                                    <div className="col button-style">
                                      <div>
                                        <Button
                                          type="button"
                                          variant="contained"
                                          color="secondary"
                                          sx={{ mt: 2, mb: 2, mr: 1 }}
                                          onClick={() => {
                                            push({ label: '', href: '', src: '' });
                                            setTimeout(() => {
                                              const newIdx = values.boostGrowth.services.length;
                                              boostServiceRefs.current[newIdx]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                            }, 50);
                                          }}
                                        >
                                          Add Service
                                        </Button>
                                        {idx !== 0 && (
                                          <Button
                                            type="button"
                                            sx={{ mt: 2, ml: 2, mb: 2 }}
                                            variant="contained"
                                            color="error"
                                            onClick={() => remove(idx)}
                                            className="home-section-delete-button"
                                          >
                                            Delete Service
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              {values.boostGrowth.services.length === 0 && (
                                <Button
                                  type="button"
                                  variant="contained"
                                  color="secondary"
                                  sx={{ mt: 1, mb: 2 }}
                                  onClick={() => {
                                    push({ label: '', href: '', src: '' });
                                    setTimeout(() => {
                                      boostServiceRefs.current[0]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 50);
                                  }}
                                >
                                  Add Service
                                </Button>
                              )}
                            </div>
                          )}
                        </FieldArray>
                      </div>

                     

                      <Grid container>
                        <Grid item xs={4} sm={4} md={3} lg={2} mt={3}>
                          <LoadingButton
                            fullWidth
                            size="large"
                            type="submit"
                            variant="contained"
                            loading={loading}
                          >
                            Submit
                          </LoadingButton>
                        </Grid>
                        <Grid item xs={4} sm={4} md={3} lg={2} ml={2} mt={3}>
                          <Button
                            fullWidth
                            size="large"
                            variant="contained"
                            color="error"
                            component={RouterLink}
                            to="/dashboard/blogs-detail-section"
                          >
                            Cancel
                          </Button>
                        </Grid>
                      </Grid>
                    </Form>
                  )}
                </Formik>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
}

export const SectionName = (props) => {
  return (
    <div style={{ margin: '15px 0 6px 0' }}>
      <Chip color={props.color ? props.color : 'success'} variant="outlined" label={props.name} />
    </div>
  );
};
